import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Area } from '@ant-design/plots';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../../api/client';
import { errMsg } from '../../lib/errors';
import {
  DEFAULT_FILTERS,
  PERIOD_OPTIONS,
  filtersDiffer,
  filtersReady,
  formatDay,
  formatDate,
  formatDuration,
  formatStamp,
  toQuery,
  type NormalUserUsageReport,
  type NormalUserUsageRow,
  type UsageFilters,
  type UsagePeriod,
} from './normalUsers';

/**
 * Normal User Analytics — registered / online / active-in-app counts and
 * measured app-usage time for NORMAL users (listeners are excluded by the
 * API, and have their own separate history on the Listeners pages).
 *
 * ── Manual fetching, on purpose ───────────────────────────────────────────
 * This page NEVER fetches on its own. The query is `enabled: false`, so
 * mounting the admin panel, opening this tab, re-rendering, remounting,
 * typing in search, changing a filter or redrawing the chart all cost zero
 * requests. The only thing that calls the API is an explicit click on
 * **Load analytics** / **Refresh**.
 *
 * How that is guaranteed rather than hoped for:
 *   - `enabled: false` + a STABLE query key. The key deliberately does not
 *     contain the filters: a changing key would make React Query drop the
 *     cached rows and blank the page the moment someone touched a filter.
 *   - the filters the request uses are read from `appliedRef` at fetch time,
 *     and that ref is only written by the click handler.
 *   - `refetchOnMount/WindowFocus/Reconnect` and `refetchInterval` are all off,
 *     so a tab switch or a laptop waking up cannot sneak a request in.
 *   - the button is disabled while `isFetching`, and React Query dedupes an
 *     in-flight refetch, so a double-click is still one request.
 *
 * Previously loaded figures stay on screen during a refresh and after a failed
 * one — a stale number plus a visible error beats an empty page.
 */
export function NormalUserAnalyticsPage() {
  // What the admin is composing. Purely local until they press the button.
  const [draft, setDraft] = useState<UsageFilters>(DEFAULT_FILTERS);
  // What the figures on screen were actually fetched with.
  const [applied, setApplied] = useState<UsageFilters | null>(null);
  const appliedRef = useRef<UsageFilters>(DEFAULT_FILTERS);

  const query = useQuery<NormalUserUsageReport>({
    // Stable on purpose — see the note above.
    queryKey: ['admin', 'analytics', 'normal-user-usage'],
    queryFn: async () => {
      const res = await api.get<NormalUserUsageReport>(
        '/admin/analytics/app-usage/normal-users',
        { params: toQuery(appliedRef.current) },
      );
      return res.data;
    },
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: false,
    gcTime: 30 * 60 * 1000,
  });

  const { refetch, isFetching, data, error } = query;

  /** The ONLY path to the network on this page. */
  const load = useCallback(() => {
    if (isFetching || !filtersReady(draft)) return; // one request per click
    appliedRef.current = draft;
    setApplied(draft);
    void refetch();
  }, [draft, isFetching, refetch]);

  const loaded = data != null;
  const stale = filtersDiffer(draft, applied);
  const customIncomplete = draft.period === 'custom' && !filtersReady(draft);

  const patch = (p: Partial<UsageFilters>) => setDraft((d) => ({ ...d, ...p }));

  const columns: ColumnsType<NormalUserUsageRow> = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{r.displayName ?? r.handle}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            @{r.handle}
            {r.phoneLast4 ? ` · ••${r.phoneLast4}` : ''}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      render: (v: string) => (
        <Typography.Text copyable={{ text: v }} style={{ fontSize: 11 }} type="secondary">
          {v.slice(0, 8)}…
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => (
        <Space size={4}>
          {r.online ? <Tag color="green">Online</Tag> : <Tag>Offline</Tag>}
          {r.activeInApp && <Tag color="blue">In app</Tag>}
        </Space>
      ),
    },
    {
      title: 'Last activity',
      dataIndex: 'lastActivityAt',
      render: (v: string | null) => formatStamp(v),
    },
    {
      title: 'App usage',
      dataIndex: 'usageSeconds',
      render: (v: number, r) => (
        <Space direction="vertical" size={0}>
          <span>{formatDuration(v)}</span>
          {r.sessions > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {r.sessions} session{r.sessions === 1 ? '' : 's'}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Registered',
      dataIndex: 'registeredAt',
      render: (v: string) => formatDate(v),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space direction="vertical" size={4}>
        <Typography.Title level={3} style={{ margin: 0, color: 'white' }}>
          Normal User Analytics
        </Typography.Title>
        <Typography.Text type="secondary">
          Normal users only — approved listeners are excluded and keep their own activity
          history on the Listeners pages.
        </Typography.Text>
      </Space>

      {/* Controls. Changing anything here only edits the draft; nothing is
          requested until the button is pressed. */}
      <Card size="small">
        <Space wrap align="end" size={12}>
          <Space direction="vertical" size={2}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Period
            </Typography.Text>
            <Select<UsagePeriod>
              value={draft.period}
              style={{ width: 160 }}
              options={PERIOD_OPTIONS}
              onChange={(period) => patch({ period, page: 0 })}
            />
          </Space>

          {draft.period === 'custom' && (
            <Space direction="vertical" size={2}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                From / to
              </Typography.Text>
              {/* Uncontrolled + `key`, like UsersPage: reads the formatted
                  strings so this file needs no dayjs value objects. */}
              <DatePicker.RangePicker
                aria-label="Usage period"
                placeholder={['From', 'To']}
                key={`${draft.from ?? ''}|${draft.to ?? ''}`}
                onChange={(_dates, [f, t]) =>
                  patch({ from: f || undefined, to: t || undefined, page: 0 })
                }
              />
            </Space>
          )}

          <Space direction="vertical" size={2}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Search
            </Typography.Text>
            <Input
              allowClear
              placeholder="Handle, name or phone last 4"
              style={{ width: 240 }}
              value={draft.q}
              // Typing never fetches — `onPressEnter` runs the same single
              // explicit action the button does.
              onChange={(e) => patch({ q: e.target.value, page: 0 })}
              onPressEnter={load}
            />
          </Space>

          <Button
            type="primary"
            icon={loaded ? <ReloadOutlined /> : undefined}
            loading={isFetching}
            disabled={isFetching || customIncomplete}
            onClick={load}
          >
            {loaded ? 'Refresh' : 'Load analytics'}
          </Button>

          {loaded && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Last refreshed {formatStamp(data.generatedAt)}
            </Typography.Text>
          )}
        </Space>

        {customIncomplete && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Pick both ends of the custom range, then press Load analytics."
          />
        )}
        {stale && !customIncomplete && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Filters changed — press Refresh to apply them."
            description="The figures below still show the previous selection."
          />
        )}
      </Card>

      {error != null && (
        <Alert
          type="error"
          showIcon
          message="Could not load analytics"
          description={`${errMsg(error, 'Request failed')}${
            loaded ? ' — the figures below are from the last successful refresh.' : ''
          }`}
        />
      )}

      {!loaded ? (
        <Card>
          <Empty
            description={
              <Space direction="vertical" size={4}>
                <Typography.Text>No analytics loaded.</Typography.Text>
                <Typography.Text type="secondary">
                  This page never fetches on its own. Press Load analytics to run the report.
                </Typography.Text>
              </Space>
            }
          >
            <Button type="primary" loading={isFetching} disabled={isFetching} onClick={load}>
              Load analytics
            </Button>
          </Empty>
        </Card>
      ) : (
        <>
          {data.trackingSince == null ? (
            <Alert
              type="info"
              showIcon
              message="No app-usage data recorded yet"
              description="Usage time is measured from app builds that report foreground activity. Counts of registered users are accurate; usage durations will stay at zero until those builds are in the field."
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message={`App-usage tracking began ${formatStamp(data.trackingSince)}`}
              description="Periods before that have no usage data — which is not the same as zero usage. Durations are measured FOREGROUND time; they do not prove someone was watching the screen, and call time is not counted as app usage."
            />
          )}

          <Row gutter={[12, 12]}>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic title="Registered normal users" value={data.summary.totalRegistered} />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic
                  title={
                    <Space size={4}>
                      Currently online
                      <Tooltip title="Has a live realtime connection. The connection can survive a trip to the background, so this means reachable — not necessarily looking at the app.">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  value={data.summary.currentlyOnline}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic
                  title={
                    <Space size={4}>
                      Active in app now
                      <Tooltip
                        title={`Reported the app in the foreground within the last ${Math.round(
                          data.activeWindowSeconds / 60,
                        )} minutes. Tracked separately from "online".`}
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  value={data.summary.currentlyActiveInApp}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic title="Active today (unique)" value={data.summary.activeToday} />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic
                  title="New registrations today"
                  value={data.summary.newRegistrationsToday}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic
                  title="New registrations (period)"
                  value={data.summary.newRegistrationsInPeriod}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic
                  title={
                    <Space size={4}>
                      Average usage
                      <Tooltip
                        title={`Total measured foreground time divided by the ${data.summary.activeUsersInPeriod} unique normal user(s) active in this period.`}
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  value={formatDuration(data.summary.averageUsageSeconds)}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card size="small">
                <Statistic
                  title="Total usage (period)"
                  value={formatDuration(data.summary.totalUsageSeconds)}
                />
              </Card>
            </Col>
          </Row>

          <Card size="small" title="Daily active normal users and usage" loading={isFetching}>
            {data.daily.length === 0 ? (
              <Empty description="No activity recorded in this period." />
            ) : (
              <Area
                height={240}
                data={data.daily.map((d) => ({
                  day: formatDay(d.day),
                  value: d.activeUsers,
                }))}
                xField="day"
                yField="value"
                // Charts render from data already in hand — no interaction
                // here can reach the network.
                tooltip={{ title: 'day' }}
              />
            )}
          </Card>

          <Card
            size="small"
            title={`Normal users (${data.total})`}
            extra={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Page {data.page + 1} · press Refresh after changing the page
              </Typography.Text>
            }
          >
            <Table<NormalUserUsageRow>
              rowKey="userId"
              size="small"
              columns={columns}
              dataSource={data.rows}
              loading={isFetching}
              pagination={{
                // Server-side: only one page is ever fetched. Changing the
                // page stages it like any other filter — the request happens
                // on the explicit Refresh click, never from this callback.
                current: draft.page + 1,
                pageSize: draft.pageSize,
                total: data.total,
                showSizeChanger: true,
                pageSizeOptions: [10, 25, 50, 100],
                onChange: (page, pageSize) => patch({ page: page - 1, pageSize }),
                showTotal: (t) => `${t} normal users`,
              }}
            />
          </Card>
        </>
      )}
    </Space>
  );
}
